import { useFormikContext } from 'formik';
import { SelectedEquipment, CrewField } from '../RaptorJobCreateModal';
import { OptionType } from './useERPEquipmentSelector';
import { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { useJobRoles, OPERATOR_ROLE } from '@/raptorjob/presentation/components/RaptorJobCreatePage/hook/useJobRoles';

interface UseERPEquipmentSelectorFiltersProps {
  availableClasses: OptionType[];
  availableTypes: OptionType[];
  availableSizes: OptionType[];
  selectedClass: OptionType | null;
  selectedType: OptionType | null;
  selectedSize: OptionType | null;
  setSelectedClass: (classOption: OptionType | null) => void;
  setSelectedType: (typeOption: OptionType | null) => void;
  setSelectedSize: (sizeOption: OptionType | null) => void;
  setSelectedEquipment: (equipmentOption: SelectedEquipment | null) => void;
  equipments: EquipmentTableData[];
  crewFields?: CrewField[];
  setCrewFields?: (fields: CrewField[]) => void;
}
export const useERPEquipmentSelectorFilters = ({
  availableClasses,
  availableTypes,
  availableSizes,
  selectedClass,
  selectedType,
  selectedSize,
  setSelectedClass,
  setSelectedType,
  setSelectedSize,
  setSelectedEquipment,
  crewFields,
  setCrewFields
}: UseERPEquipmentSelectorFiltersProps) => {
  const { setFieldValue, setFieldTouched } = useFormikContext<ERPJobFormValues>();
  const { supportedJobRoles } = useJobRoles();
  const getFilteredTypeOrSizeOptionsByClass = (
    classId: string | number | null | undefined,
    availableOptions: OptionType[]
  ): OptionType[] => {
    if (!classId) {
      return availableOptions;
    }

    return availableOptions.filter((option) => {
      const optionWithClass = option as { class?: { id?: number | null } | null };
      return optionWithClass?.class?.id === Number(classId);
    });
  };

  const filteredClasses = availableClasses;

  const filteredTypes = getFilteredTypeOrSizeOptionsByClass(selectedClass?.id, availableTypes);

  const filteredSizesByClass = getFilteredTypeOrSizeOptionsByClass(selectedClass?.id, availableSizes);

  // Ensure selected size is always included in filtered sizes, even if it doesn't match the class filter
  // This is important when selecting equipment, as the size might be set before the class filter is applied
  const filteredSizes = (() => {
    if (!selectedSize?.id) {
      return filteredSizesByClass;
    }

    const isSelectedSizeInFiltered = filteredSizesByClass.some((size) => size.id === selectedSize.id);
    if (isSelectedSizeInFiltered) {
      return filteredSizesByClass;
    }

    // If selected size is not in filtered list, add it so it appears in the select
    return [...filteredSizesByClass, selectedSize];
  })();

  const handleClassChange = (newClass: OptionType | null) => {
    setSelectedClass(newClass);
    setFieldValue('equipmentClassId', newClass?.id ? Number(newClass.id) : null);
    // Deselect equipment when class changes manually
    setSelectedEquipment(null);
    setFieldValue('equipmentUnitId', null);

    if (newClass?.id) {
      const newFilteredTypes = getFilteredTypeOrSizeOptionsByClass(newClass.id, availableTypes);
      const newFilteredSizes = getFilteredTypeOrSizeOptionsByClass(newClass.id, availableSizes);

      if (selectedType?.id && !newFilteredTypes.some((type) => type.id === selectedType.id)) {
        setSelectedType(null);
        setFieldValue('equipmentTypeId', null);
      }
      if (selectedSize?.id && !newFilteredSizes.some((size) => size.id === selectedSize.id)) {
        setSelectedSize(null);
        setFieldValue('equipmentSizeId', null);
      }
    }
  };

  const handleTypeChange = (newType: OptionType | null) => {
    setSelectedType(newType);
    setFieldValue('equipmentTypeId', newType?.id ? Number(newType.id) : null);
    // Deselect equipment when type changes manually
    setSelectedEquipment(null);
    setFieldValue('equipmentUnitId', null);
  };

  const handleSizeChange = async (newSize: OptionType | null) => {
    setSelectedSize(newSize);
    await setFieldValue('equipmentSizeId', newSize?.id ? Number(newSize.id) : null, true);
    await setFieldTouched('equipmentSizeId', true, false);
    // Deselect equipment when size changes manually
    setSelectedEquipment(null);
    setFieldValue('equipmentUnitId', null);
  };

  const handleEquipmentSelect = (equipment: SelectedEquipment) => {
    // Set equipmentUnitId to equipment ID when equipment is selected
    setSelectedEquipment(equipment);
    setFieldValue('equipmentUnitId', equipment.id ? Number(equipment.id) : null);

    if (equipment.class?.id && equipment.class?.name) {
      setSelectedClass({ id: equipment.class.id, name: equipment.class.name });
      setFieldValue('equipmentClassId', equipment.class.id ? Number(equipment.class.id) : null);
    }
    if (equipment.type?.id && equipment.type?.name) {
      setSelectedType({ id: equipment.type.id, name: equipment.type.name });
      setFieldValue('equipmentTypeId', equipment.type.id ? Number(equipment.type.id) : null);
    }
    if (equipment.size?.id && equipment.size?.name) {
      setSelectedSize({ id: equipment.size.id, name: equipment.size.name });
      setFieldValue('equipmentSizeId', equipment.size.id ? Number(equipment.size.id) : null);
    }

    // Create crew entry when equipment has defaultEmployee
    const equipmentDefaultEmployee = equipment.defaultEmployee;
    const isEmployeeInCrewList = crewFields && crewFields.some((crew) => crew.employeeId === equipmentDefaultEmployee?.id);

    if (equipmentDefaultEmployee?.id && !isEmployeeInCrewList && crewFields && setCrewFields) {
      const operatorRole = supportedJobRoles?.find((role: { id: number; name?: string | null }) => role.id === OPERATOR_ROLE.id);
      const roleName = operatorRole?.name || OPERATOR_ROLE.name;

      const newCrewField: CrewField = {
        rowId: `crew-${Date.now()}`,
        employeeId: equipmentDefaultEmployee.id,
        employeeName: equipmentDefaultEmployee.fullName || '',
        roleId: OPERATOR_ROLE.id,
        roleName
      };

      setCrewFields([...crewFields, newCrewField]);
    }
  };

  const handleEquipmentDeselect = () => {
    setSelectedEquipment(null);
    // Clear equipmentUnitId when equipment is deselected
    setFieldValue('equipmentUnitId', null);
  };

  const handleClearFilters = () => {
    setSelectedClass(null);
    setSelectedType(null);
    setSelectedSize(null);
  };

  return {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    handleClearFilters
  };
};
